import { useState } from "react";
import {
  FileArrowUp,
  PencilSimpleLine,
  CheckCircle,
  XCircle,
  ArrowCounterClockwise,
  Lock,
  Trash,
  DownloadSimple,
  Clock,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  documentStatusTone,
  expiryTone,
  formatExpiry,
} from "@/lib/documents";

/**
 * Phase 3 Module 3 · Shared document-requirement row.
 *
 * Works for both family-scope and child-scope requirements. Props:
 *   row                   — { type, documents, latest, effectiveStatus, expiryState }
 *   role                  — 'parent' | 'admin'
 *   onUpload(file)        — async; only meaningful when requires_signature=false
 *   onSign(signedName)    — async; only meaningful when requires_signature=true
 *   onDelete(doc)         — async; parent can delete uploaded docs, admin can delete any
 *   onReview(doc, status, note) — admin-only approve / reject
 *   onDownload(doc)       — async, resolves to a signed URL
 *   scopeLabel            — 'family' | 'child' (for empty-state copy)
 */
export default function DocumentRequirementRow({
  row,
  role = "parent",
  onUpload,
  onSign,
  onDelete,
  onReview,
  onDownload,
}) {
  const { type, documents, latest, effectiveStatus, expiryState } = row;
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signedName, setSignedName] = useState("");
  const tone = documentStatusTone(effectiveStatus);
  const expiry = expiryTone(expiryState);

  const canUpload = role === "parent" || role === "admin";
  const canSign = role === "parent" && type?.requires_signature === true;

  async function handleFile(e) {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-select of same file later
    if (!f || !onUpload) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large — limit is 10 MB.");
      return;
    }
    try {
      setBusy(true);
      await onUpload(f);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleSign() {
    if (!signedName.trim()) {
      toast.error("Please type your full name to sign.");
      return;
    }
    try {
      setBusy(true);
      await onSign(signedName.trim());
      toast.success("Signed");
      setSignOpen(false);
      setSignedName("");
    } catch (err) {
      toast.error(err?.message || "Could not save signature");
    } finally {
      setBusy(false);
    }
  }

  async function handleReview(doc, status) {
    let note = null;
    if (status === "rejected") {
      // eslint-disable-next-line no-alert
      note = window.prompt("Reason for rejection (shown to the parent):", "") || null;
    }
    try {
      setBusy(true);
      await onReview(doc, status, note);
      toast.success(status === "approved" ? "Approved" : "Rejected");
    } catch (err) {
      toast.error(err?.message || "Could not update status");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(doc) {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      setBusy(true);
      await onDelete(doc);
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.message || "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(doc) {
    if (!onDownload || !doc.storage_path) return;
    try {
      const url = await onDownload(doc);
      if (url) window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(err?.message || "Could not open file");
    }
  }

  return (
    <article
      data-testid={`doc-req-${type.code}`}
      data-status={effectiveStatus}
      data-expiry={expiryState || ""}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              data-testid={`doc-req-${type.code}-label`}
              className="font-heading text-base font-semibold tracking-tight text-neutral-900"
            >
              {type.label}
            </h3>
            {type.is_required ? (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                Required
              </span>
            ) : (
              <span className="rounded-full bg-neutral-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                Optional
              </span>
            )}
            {type.requires_signature && (
              <span className="flex items-center gap-1 rounded-full bg-secondary/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-700">
                <PencilSimpleLine size={10} weight="bold" aria-hidden />
                E-sign
              </span>
            )}
            <span
              data-testid={`doc-req-${type.code}-status-chip`}
              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone.bg} ${tone.color} ${tone.ring}`}
            >
              {tone.label}
            </span>
            {expiry && (
              <span
                data-testid={`doc-req-${type.code}-expiry-chip`}
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${expiry.bg} ${expiry.color} ${expiry.ring}`}
              >
                {expiryState === "expired" ? (
                  <WarningCircle size={10} weight="bold" aria-hidden />
                ) : (
                  <Clock size={10} weight="bold" aria-hidden />
                )}
                {expiry.label}
              </span>
            )}
          </div>
          {type.description && (
            <p className="mt-1 text-sm leading-relaxed text-neutral-600">
              {type.description}
            </p>
          )}
          {latest?.review_note && effectiveStatus === "rejected" && (
            <p
              data-testid={`doc-req-${type.code}-review-note`}
              className="mt-2 rounded-xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-2 text-xs text-[#C0443A]"
            >
              <strong>Reviewer note:</strong> {latest.review_note}
            </p>
          )}
          {latest?.expires_at && (
            <p className="mt-1 text-xs text-neutral-500">
              Valid through <strong>{formatExpiry(latest.expires_at)}</strong>
              {latest.signed_at && (
                <>
                  {" · "}signed by <strong>{latest.signed_name}</strong> on{" "}
                  {formatExpiry(latest.signed_at)}
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {/* Primary action */}
          {canSign && !signOpen && (
            <button
              type="button"
              data-testid={`doc-req-${type.code}-sign-open`}
              disabled={busy}
              onClick={() => setSignOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
            >
              <PencilSimpleLine size={14} weight="bold" />
              {latest ? "Re-sign" : "Sign now"}
            </button>
          )}
          {!canSign && canUpload && (
            <label
              data-testid={`doc-req-${type.code}-upload-label`}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-neutral-800 hover:border-neutral-300 hover:bg-neutral-50 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
            >
              <FileArrowUp size={14} weight="bold" />
              {latest ? "Re-upload" : "Upload"}
              <input
                type="file"
                className="sr-only"
                data-testid={`doc-req-${type.code}-upload-input`}
                disabled={busy}
                onChange={handleFile}
              />
            </label>
          )}
        </div>
      </div>

      {/* Inline sign form */}
      {signOpen && (
        <div
          data-testid={`doc-req-${type.code}-sign-panel`}
          className="mt-4 rounded-2xl border border-secondary/40 bg-secondary/10 p-4"
        >
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
            <Lock size={12} weight="duotone" />
            Type your full legal name to sign
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              data-testid={`doc-req-${type.code}-sign-input`}
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Your full legal name"
              className="flex-1 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
            <button
              type="button"
              data-testid={`doc-req-${type.code}-sign-submit`}
              disabled={busy || !signedName.trim()}
              onClick={handleSign}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
            >
              Save signature
            </button>
            <button
              type="button"
              onClick={() => {
                setSignOpen(false);
                setSignedName("");
              }}
              className="rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Admin review + per-doc history */}
      {(role === "admin" || documents.length > 1) && documents.length > 0 && (
        <ul
          data-testid={`doc-req-${type.code}-history`}
          className="mt-4 space-y-2 border-t border-neutral-100 pt-3"
        >
          {documents.map((doc) => {
            const t = documentStatusTone(doc.status);
            return (
              <li
                key={doc.id}
                data-testid={`doc-req-${type.code}-row-${doc.id}`}
                data-status={doc.status}
                className="flex items-center justify-between gap-3 rounded-xl bg-neutral-50 p-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-neutral-700">
                    {doc.signed_at
                      ? `Signed — ${doc.signed_name || "—"}`
                      : doc.storage_path
                        ? doc.storage_path.split("/").pop()
                        : "—"}
                  </p>
                  <p className="text-[11px] text-neutral-500">
                    {new Date(doc.created_at).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${t.bg} ${t.color} ${t.ring}`}
                >
                  {t.label}
                </span>
                {doc.storage_path && (
                  <button
                    type="button"
                    data-testid={`doc-req-${type.code}-row-${doc.id}-download`}
                    onClick={() => handleDownload(doc)}
                    title="Open file"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-600 ring-1 ring-neutral-200 hover:bg-neutral-100"
                  >
                    <DownloadSimple size={13} weight="bold" />
                  </button>
                )}
                {role === "admin" && doc.status !== "approved" && (
                  <button
                    type="button"
                    data-testid={`doc-req-${type.code}-row-${doc.id}-approve`}
                    disabled={busy}
                    onClick={() => handleReview(doc, "approved")}
                    title="Approve"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary-hover ring-1 ring-primary/30 hover:bg-primary/20"
                  >
                    <CheckCircle size={14} weight="bold" />
                  </button>
                )}
                {role === "admin" && doc.status !== "rejected" && (
                  <button
                    type="button"
                    data-testid={`doc-req-${type.code}-row-${doc.id}-reject`}
                    disabled={busy}
                    onClick={() => handleReview(doc, "rejected")}
                    title="Reject"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#C6851E]/10 text-[#C6851E] ring-1 ring-[#C6851E]/30 hover:bg-[#C6851E]/20"
                  >
                    <XCircle size={14} weight="bold" />
                  </button>
                )}
                {role === "admin" && doc.status === "rejected" && (
                  <button
                    type="button"
                    data-testid={`doc-req-${type.code}-row-${doc.id}-revoke`}
                    disabled={busy}
                    onClick={() => handleReview(doc, "approved")}
                    title="Revoke rejection"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
                  >
                    <ArrowCounterClockwise size={13} weight="bold" />
                  </button>
                )}
                {(role === "admin" ||
                  (doc.uploaded_by && doc.status === "uploaded")) && (
                  <button
                    type="button"
                    data-testid={`doc-req-${type.code}-row-${doc.id}-delete`}
                    disabled={busy}
                    onClick={() => handleDelete(doc)}
                    title="Delete"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-500 ring-1 ring-neutral-200 hover:bg-neutral-100 hover:text-[#C6851E]"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}
